import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  BuildingOfficeIcon,
  ShoppingBagIcon,
  HomeIcon,
  BeakerIcon,
  AcademicCapIcon,
  TruckIcon,
  ScaleIcon,
  BuildingStorefrontIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
  PresentationChartLineIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const IndustriesPage: React.FC = () => {
  const navigate = useNavigate();
  useSEO(pageConfigs.industries);

  // Navigation handlers
  const handleViewCaseStudies = () => {
    navigate('/case-studies');
  };

  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };
  const industries = [
    {
      name: "Healthcare",
      icon: <BeakerIcon className="w-12 h-12" />,
      emoji: "🏥",
      clients: "50+",
      description: "Digital marketing solutions for healthcare providers, medical practices, and healthcare technology companies.",
      services: ["HIPAA-compliant marketing", "Patient acquisition", "Telehealth promotion", "Provider reputation management"],
      results: ["45% increase in patient inquiries", "320% ROI on marketing spend", "Top rankings for medical keywords"]
    },
    {
      name: "Financial Services",
      icon: <BuildingOfficeIcon className="w-12 h-12" />,
      emoji: "🏦",
      clients: "75+",
      description: "Specialized digital marketing for banks, credit unions, financial advisors, and fintech companies.",
      services: ["Compliance-focused marketing", "Lead generation", "Financial education content", "Trust building campaigns"],
      results: ["65% increase in qualified leads", "28% reduction in acquisition costs", "185% growth in client assets"]
    },
    {
      name: "E-commerce",
      icon: <ShoppingBagIcon className="w-12 h-12" />,
      emoji: "🛒",
      clients: "120+",
      description: "Comprehensive digital marketing for online retailers, DTC brands, and marketplace sellers.",
      services: ["Conversion optimization", "Shopping campaigns", "Marketplace management", "Email marketing automation"],
      results: ["300% increase in online sales", "45% improvement in ROAS", "65% reduction in cart abandonment"]
    },
    {
      name: "Real Estate",
      icon: <HomeIcon className="w-12 h-12" />,
      emoji: "🏢",
      clients: "85+",
      description: "Targeted digital marketing for real estate agents, brokers, property managers, and developers.",
      services: ["Property listing promotion", "Agent branding", "Neighborhood targeting", "Virtual tour marketing"],
      results: ["250% increase in property inquiries", "42% reduction in days on market", "185% growth in client roster"]
    },
    {
      name: "Technology",
      icon: <BeakerIcon className="w-12 h-12" />,
      emoji: "💻",
      clients: "95+",
      description: "Strategic digital marketing for SaaS companies, tech startups, and established technology firms.",
      services: ["Product launch campaigns", "User acquisition", "Technical content marketing", "Conversion funnel optimization"],
      results: ["320% increase in trial signups", "45% reduction in CAC", "85% improvement in activation rates"]
    },
    {
      name: "Manufacturing",
      icon: <TruckIcon className="w-12 h-12" />,
      emoji: "🏭",
      clients: "60+",
      description: "Specialized B2B digital marketing for manufacturers, industrial suppliers, and equipment providers.",
      services: ["Technical lead generation", "Industry content marketing", "Trade show support", "Dealer/distributor marketing"],
      results: ["185% increase in qualified leads", "65% improvement in sales cycle efficiency", "320% ROI on marketing spend"]
    },
    {
      name: "Professional Services",
      icon: <ScaleIcon className="w-12 h-12" />,
      emoji: "⚖️",
      clients: "110+",
      description: "Targeted digital marketing for law firms, accounting practices, consultants, and service providers.",
      services: ["Authority building", "Lead qualification", "Thought leadership", "Referral marketing"],
      results: ["250% increase in case inquiries", "45% growth in retainer clients", "185% improvement in lead quality"]
    },
    {
      name: "Hospitality",
      icon: <BuildingStorefrontIcon className="w-12 h-12" />,
      emoji: "🏨",
      clients: "40+",
      description: "Comprehensive digital marketing for hotels, restaurants, tourism, and hospitality businesses.",
      services: ["Booking optimization", "Local SEO", "Reputation management", "Experience marketing"],
      results: ["320% increase in direct bookings", "65% reduction in OTA commissions", "45% improvement in occupancy rates"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.industries} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight pt-2.5">
              Industry-Specific 
              <span className="text-blue-600"> Digital Marketing</span>
            </h1>
            
            <p className="text-xl text-slate-700 mb-8 leading-relaxed">
              We understand the unique challenges and opportunities in your industry.
              Our specialized teams deliver tailored digital marketing solutions that drive results.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2">
                <span>Schedule Industry Consultation</span>
                <ArrowRightIcon className="w-5 h-5" />
              </button>
              <button className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors">
                View Case Studies
              </button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-1">8+</div>
                <div className="text-sm font-semibold text-slate-700">Industries Served</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-1">635+</div>
                <div className="text-sm font-semibold text-slate-700">Industry Clients</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-1">15+</div>
                <div className="text-sm font-semibold text-slate-700">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-1">250%</div>
                <div className="text-sm font-semibold text-slate-700">Average ROI</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Specialized digital marketing expertise across diverse industries
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{industry.icon}</div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{industry.name}</h3>
                      <div className="text-sm font-semibold bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                        {industry.clients} Clients
                      </div>
                    </div>
                    <p className="text-slate-600 mb-4">{industry.description}</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Specialized Services:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {industry.services.map((service, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <CheckIcon className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-slate-700">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Client Results:</h4>
                  <div className="space-y-2">
                    {industry.results.map((result, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <StarIcon className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-slate-700">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <button 
                    onClick={handleViewCaseStudies}
                    className="w-full bg-white hover:bg-slate-100 text-slate-900 font-semibold py-3 px-6 rounded-lg border border-slate-200 transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>View {industry.name} Case Studies</span>
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              Our Industry-Specific Approach
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              How we deliver tailored digital marketing solutions for your industry
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <BeakerIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-4">Industry Research</h3>
              <p className="text-slate-600">
                We conduct in-depth research to understand your industry's unique challenges, opportunities, regulations, and customer behaviors.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AcademicCapIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-4">Specialized Expertise</h3>
              <p className="text-slate-600">
                Our industry-specific teams bring deep knowledge and experience in your sector, ensuring relevant strategies and execution.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <PresentationChartLineIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-display font-bold text-slate-900 mb-4">Tailored Solutions</h3>
              <p className="text-slate-600">
                We develop customized digital marketing strategies that address your industry's specific needs and target your ideal customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              What Our Industry Clients Say
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Hear from clients across different industries
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-50 rounded-xl p-8 border border-slate-100">
              <div className="flex items-center space-x-4 mb-6">
                <div className="text-4xl">🏥</div>
                <div>
                  <h4 className="font-bold text-slate-900">Dr. Sarah Johnson</h4>
                  <p className="text-slate-600 text-sm">Medical Director, Johnson Health</p>
                </div>
              </div>
              <p className="text-slate-700 mb-6">
                "3 Arrows Media understands the unique challenges of healthcare marketing. Their HIPAA-compliant strategies have helped us grow our patient base by 45% while maintaining the highest standards of privacy and compliance."
              </p>
              <div className="flex text-amber-400 text-xl">
                ★★★★★
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-8 border border-slate-100">
              <div className="flex items-center space-x-4 mb-6">
                <div className="text-4xl">🏦</div>
                <div>
                  <h4 className="font-bold text-slate-900">Michael Rodriguez</h4>
                  <p className="text-slate-600 text-sm">CEO, First Capital Financial</p>
                </div>
              </div>
              <p className="text-slate-700 mb-6">
                "The team at 3 Arrows Media has transformed our digital presence. Their understanding of financial services marketing regulations and their ability to generate qualified leads has been instrumental in our growth."
              </p>
              <div className="flex text-amber-400 text-xl">
                ★★★★★
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-8 border border-slate-100">
              <div className="flex items-center space-x-4 mb-6">
                <div className="text-4xl">🛒</div>
                <div>
                  <h4 className="font-bold text-slate-900">Jennifer Chen</h4>
                  <p className="text-slate-600 text-sm">Founder, StyleHub E-commerce</p>
                </div>
              </div>
              <p className="text-slate-700 mb-6">
                "Our e-commerce business has seen a 300% increase in sales since working with 3 Arrows Media. Their expertise in conversion optimization and marketplace management has been game-changing for our brand."
              </p>
              <div className="flex text-amber-400 text-xl">
                ★★★★★
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-display font-bold mb-6">
              Ready for Industry-Specific Digital Marketing?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Schedule a consultation with our industry specialists to discuss your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleScheduleConsultation}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors"
              >
                Schedule Consultation
              </button>
              <button 
                onClick={handleViewCaseStudies}
                className="bg-transparent hover:bg-white/10 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/20 transition-colors"
              >
                View Case Studies
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustriesPage;